import UserContext from "Context/UserProvider";
import { ChargingPartner } from "components/charging/BusinessChargingNetworkParameters/chargingPartner";
import DriverInstructionsPanel from "components/charging/ChargingPartnerComponent/DriverInstructionsPanel";
import PartnershipContractualDocumentsPanel from "components/charging/ChargingPartnerComponent/PartnershipContractualDocumentsPanel";
import { useContext, useEffect, useState } from "react";
import toast from 'react-hot-toast';
import { useLocation, useNavigate } from "react-router-dom";
import WeeklyChargingReservation from "../../components/charging/BusinessChargingNetworkParameters/WeeklyChargerReservation";
import { WeekDay, weekDays as initialWeekDays } from "../../components/charging/BusinessChargingNetworkParameters/weekDays";
import {
    Backdrop,
    Box,
    Button,
    CircularProgress,
    Container,
    Grid,
    Typography,
    ButtonGroup,
} from '@mui/material';
import {
    AccessTime as AccessTimeIcon,
    AttachMoney as AttachMoneyIcon,
    Check as CheckIcon,
    Close as CloseIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';
import DashboardCard from "components/dashboard/common/DashboardCard";
import { useApiService } from "../../services/ApiService/ApiService";

function ChargingPartnerReviewOfferPage() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const { userInfo } = useContext(UserContext);
    const { fetchWithAuth } = useApiService();

    const defaultOffer: ChargingPartner = {
        uuid: "",
        ramsDocuments: [],
        questions: [],
        reservationOption: "",
        usageRestriction: false,
        chargersReservedFromHour: "",
        chargersReservedToHour: "",
        energyCostAcSinglePhaseGbpPerKWhOffPeak: 0,
        energyCostAcThreePhaseGbpPerKWhOffPeak: 0,
        energyCostDcGbpPerKWhOffPeak: 0,
        energyCostAcSinglePhaseGbpPerKWhPeak: 0,
        energyCostAcThreePhaseGbpPerKWhPeak: 0,
        energyCostDcGbpPerKWhPeak: 0,
        partnerUuid: "",
        providerBusinessUuid: "",
        businessName: "",
        consumerBusinessUuid: "",
        workflowState: "",
        visitorWorkflowState: "",
        energyPriceDcGbpPerKWhOffPeak: 0,
        energyPriceAcThreePhaseGbpPerKWhOffPeak: 0,
        energyPriceAcSinglePhaseGbpPerKWhOffPeak: 0,
        energyPriceDcGbpPerKWhPeak: 0,
        energyPriceAcThreePhaseGbpPerKWhPeak: 0,
        energyPriceAcSinglePhaseGbpPerKWhPeak: 0,
        dayOfWeekReservation: [],
        selectedRateCard: {
            chargingPartnerCount: 0,
            energyCostAcSinglePhaseGbpPerKWhOffPeak: 0,
            energyCostAcSinglePhaseGbpPerKWhPeak: 0,
            energyCostAcThreePhaseGbpPerKWhOffPeak: 0,
            energyCostAcThreePhaseGbpPerKWhPeak: 0,
            energyCostDcGbpPerKWhOffPeak: 0,
            energyCostDcGbpPerKWhPeak: 0,
            name: "",
            uuid: "",
            initial: false
        },
        rateCards: [],
        selectedFrequency: "",
        approved: false,
        requested: false,
        chargingPartnerLocations: [],
        businessLocations: [],
    };

    const [chargingPartnerOffer, setChargingPartnerOffer] = useState<ChargingPartner>(defaultOffer);
    const location = useLocation();
    const params = location.state as any;
    const [isDisabled, setIsDisabled] = useState(false);
    const [weekDays, setWeekDays] = useState(initialWeekDays);

    // Accessing the state passed through navigation
    const state = location.state as {
        chargingPartnerUuid: string;
        chargingPartnerBusinessName: string;
        workflowState: string;
        providerBusinessUuid: string;
        consumerBusinessUuid: string;
        requestType: string;
    };

    const navigate = useNavigate();

    const handleTimeChange = (dayIndex: any, field: any, value: any) => {
        setWeekDays(prevDays =>
            prevDays.map((day, index) =>
                index === dayIndex
                    ? { ...day, [field]: value }
                    : day
            )
        );
    };

    useEffect(() => {
        fetchOffer(state.providerBusinessUuid, state.consumerBusinessUuid, state.chargingPartnerUuid);
    }, []);

    async function fetchOffer(providerBusinessUuid: string, consumerBusinessUuid: string, chargingPartnerUuid: string) {
        try {
            setSaving(true);
            const rootUrl = process.env.NODE_ENV === "production" ? process.env.REACT_APP_BACKEND_URL : window.location.origin;
            const response = await fetchWithAuth(`/charging-partner/offer/get`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    uuid: userInfo.uuid,
                    workflowState: state.workflowState,
                    providerBusinessUuid,
                    consumerBusinessUuid,
                    chargingPartnerUuid,
                }),
            });

            const res_json = await response.json();
            setChargingPartnerOffer(res_json.chargingPartner.supplier);
            setWeekDays(res_json.chargingPartner.supplier.dayOfWeekReservation);
        } catch (error) {
            console.error('Error fetching offer:', error);
            toast.error('Failed to fetch charging partner offer');
        } finally {
            setLoading(false);
            setSaving(false);
        }
    }

    async function approve() {
        try {
            setIsDisabled(true);
            setSaving(true);
            const rootUrl = process.env.NODE_ENV === "production" ? process.env.REACT_APP_BACKEND_URL : window.location.origin;
            const response = await fetchWithAuth(`/charging-partner/offer/decision/approve`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    uuid: userInfo.uuid,
                    providerBusinessUuid: state.providerBusinessUuid,
                    consumerBusinessUuid: state.consumerBusinessUuid,
                }),
            });

            if (response.status === 200) {
                toast.success('Charging Partner Approved');
                navigate("/share/business/charging-partners");
            }
        } catch (error) {
            console.error('Error approving offer:', error);
            toast.error('Failed to approve charging partner');
        } finally {
            setIsDisabled(false);
            setSaving(false);
        }
    }

    async function reject() {
        try {
            setIsDisabled(true);
            setSaving(true);
            const rootUrl = process.env.NODE_ENV === "production" ? process.env.REACT_APP_BACKEND_URL : window.location.origin;
            const response = await fetchWithAuth(`/charging-partner/offer/decision/reject`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    uuid: userInfo.uuid,
                    providerBusinessUuid: state.providerBusinessUuid,
                    consumerBusinessUuid: state.consumerBusinessUuid,
                    chargingPartnerUuid: state.chargingPartnerUuid,
                }),
            });

            if (response.status === 200) {
                toast.success('Charging Partner Rejected');
                navigate("/share/business/charging-partners");
            }
        } catch (error) {
            console.error('Error rejecting offer:', error);
            toast.error('Failed to reject charging partner');
        } finally {
            setIsDisabled(false);
            setSaving(false);
        }
    }

    async function withdraw() {
        try {
            setIsDisabled(true);
            setSaving(true);
            const rootUrl = process.env.NODE_ENV === "production" ? process.env.REACT_APP_BACKEND_URL : window.location.origin;
            const response = await fetchWithAuth(`/charging-partner/offer/withdraw`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    uuid: userInfo.uuid,
                    providerBusinessUuid: state.providerBusinessUuid,
                    consumerBusinessUuid: state.consumerBusinessUuid,
                }),
            });

            if (response.status === 200) {
                toast.success('Charging Partner Offer Withdrawn');
                navigate("/share/business/charging-partners");
            }
        } catch (error) {
            console.error('Error withdrawing offer:', error);
            toast.error('Failed to withdraw charging partner offer');
        } finally {
            setIsDisabled(false);
            setSaving(false);
        }
    }

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%', height: '100%' }}>
            <Backdrop open={saving} sx={{ zIndex: 1300 }}>
                <CircularProgress />
            </Backdrop>

            <Container maxWidth="xl">
                <Box sx={{ pr: 3 }}>
                    <Grid container spacing={3}>
                        {/* Charging Restrictions Card */}
                        <Grid item xs={12} md={6} xl={4}>
                            <DashboardCard
                                title="Charging Restrictions"
                                icon={<AccessTimeIcon />}
                                accentColor="primary"
                            >
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    {!chargingPartnerOffer.usageRestriction ? (
                                        <Typography>No charging time restrictions</Typography>
                                    ) : chargingPartnerOffer.reservationOption === "DEFAULT" ? (
                                        <>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Typography>Reservation Option:</Typography>
                                                <Typography>{chargingPartnerOffer.reservationOption}</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Typography>No Charging From:</Typography>
                                                <Typography>{chargingPartnerOffer.chargersReservedFromHour}</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Typography>No Charging Until:</Typography>
                                                <Typography>{chargingPartnerOffer.chargersReservedToHour}</Typography>
                                            </Box>
                                        </>
                                    ) : (
                                        <Box>
                                            {weekDays.map((dayInfo: WeekDay) => (
                                                <WeeklyChargingReservation
                                                    readOnly={true}
                                                    key={dayInfo.dayIndex}
                                                    dayOfWeekLabel={dayInfo.day}
                                                    dayInfo={dayInfo}
                                                    onTimeChange={handleTimeChange}
                                                />
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                            </DashboardCard>
                        </Grid>

                        {/* Off-Peak Prices Card */}
                        <Grid item xs={12} md={6} xl={4}>
                            <DashboardCard
                                title={state.requestType === 'FROM'
                                    ? `Charging Cost at ${params.chargingPartnerBusinessName} locations per kW/h (Off-Peak)`
                                    : 'Off-Peak prices'}
                                icon={<AttachMoneyIcon />}
                                accentColor="info"
                            >
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography>AC Single Phase:</Typography>
                                        <Typography>£{chargingPartnerOffer.energyCostAcSinglePhaseGbpPerKWhOffPeak}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography>AC Three Phase:</Typography>
                                        <Typography>£{chargingPartnerOffer.energyCostAcThreePhaseGbpPerKWhOffPeak}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography>DC:</Typography>
                                        <Typography>£{chargingPartnerOffer.energyCostDcGbpPerKWhOffPeak}</Typography>
                                    </Box>
                                </Box>
                            </DashboardCard>
                        </Grid>

                        {/* Peak Prices Card */}
                        <Grid item xs={12} md={6} xl={4}>
                            <DashboardCard
                                title={state.requestType === 'FROM'
                                    ? `Charging Cost at ${params.chargingPartnerBusinessName} locations per kW/h (Peak)`
                                    : 'Peak prices'}
                                icon={<AttachMoneyIcon />}
                                accentColor="warning"
                            >
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography>AC Single Phase:</Typography>
                                        <Typography>£{chargingPartnerOffer.energyCostAcSinglePhaseGbpPerKWhPeak}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography>AC Three Phase:</Typography>
                                        <Typography>£{chargingPartnerOffer.energyCostAcThreePhaseGbpPerKWhPeak}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography>DC:</Typography>
                                        <Typography>£{chargingPartnerOffer.energyCostDcGbpPerKWhPeak}</Typography>
                                    </Box>
                                </Box>
                            </DashboardCard>
                        </Grid>

                        {/* Partnership Documents */}
                        <Grid item xs={12}>
                            <PartnershipContractualDocumentsPanel
                                chargingPartnerOffer={chargingPartnerOffer}
                                panelTitle="Partnership Documents"
                                type="DOCUMENT"
                            />
                        </Grid>

                        {/* Action Buttons */}
                        <Grid item xs={12}>
                            <Box display="flex" justifyContent="center" mt={2}>
                                {state.requestType === "TO" ? (
                                    <Button
                                        variant="contained"
                                        color="error"
                                        startIcon={<DeleteIcon />}
                                        onClick={withdraw}
                                        disabled={isDisabled}
                                        size="large"
                                    >
                                        Withdraw
                                    </Button>
                                ) : state.requestType === "FROM" ? (
                                    <ButtonGroup variant="contained" size="large">
                                        <Button
                                            color="primary"
                                            startIcon={<CheckIcon />}
                                            onClick={approve}
                                            disabled={isDisabled}
                                        >
                                            Approve
                                        </Button>
                                        <Button
                                            color="error"
                                            startIcon={<CloseIcon />}
                                            onClick={reject}
                                            disabled={isDisabled}
                                        >
                                            Reject
                                        </Button>
                                    </ButtonGroup>
                                ) : null}
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            </Container>
        </Box>
    );
}

export default ChargingPartnerReviewOfferPage;
